import { useIntl } from "react-intl";
import { PricingPlanComparePlansItemFragment } from "src/graphql-types";

import { CONTACT_CTA_URL } from "@/components/wpl/pricing/src/price-finder/constants";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { EnterpriseCard } from "@/components/wpl/pricing/src/price-finder-jfind1384/enterprise-card";
import { jiraOfferingSkus } from "@/components/wpl/pricing/src/price-finder-v3/configs";
import {
    useSelector,
    useBillingCycle
} from "@/components/wpl/pricing/src/price-finder-v3/hooks.ts";
import * as selectors from "@/components/wpl/pricing/src/price-finder-v3/selectors";
import {
    usePricingPlanComparison,
    usePricingPlanCta
} from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { useLocalizedHref } from "@/libraries/web-framework";

import { DATA_PRODUCT_KEY } from "./utils/constants";

export const PlanEnterprise = () => {
    const intl = useIntl();
    const unitCount = useSelector((ctx) => selectors.getUnitCount(ctx));
    const [billingCycle, setBillingCycle] = useBillingCycle();
    const isUnitCountAboveMaxThreshold = useSelector((ctx) =>
        selectors.isUnitCountAboveMaxThreshold(ctx, jiraOfferingSkus.premium)
    );
    const recommended = useSelector((ctx) =>
        selectors.isOfferingRecommended(ctx, jiraOfferingSkus.enterprise)
    );
    const isUnitCountAboveEnterpriseMinThreshold = useSelector(
        selectors.isUnitCountAboveEnterpriseMinThreshold
    );
    const totalPrice = useSelector((ctx) =>
        selectors.getPlanTotalPriceFormatted(ctx, jiraOfferingSkus.enterprise)
    );
    const unitTierLowerBound = useSelector((ctx) =>
        selectors.getUnitTierLowerBound(ctx, jiraOfferingSkus.enterprise)
    );
    const unitTierUpperBound = useSelector((ctx) =>
        selectors.getUnitTierUpperBound(ctx, jiraOfferingSkus.enterprise)
    );

    const enterpriseContent: PricingPlanComparePlansItemFragment | undefined =
        usePricingPlanComparison("Enterprise");
    const enterprisePlanFeatureMessages = createPlanFeatureMessages(
        { enterprise: enterpriseContent },
        "enterprise"
    );

    const getPricingPlanCta = usePricingPlanCta();
    const contentfulCta = getPricingPlanCta({ planName: "Enterprise" });
    const ctaText = contentfulCta?.ctaButtonText;
    const ctaUrl = useLocalizedHref(CONTACT_CTA_URL);

    const planDescription = intl.formatMessage({
        id: "Pricing.PriceCard.Enterprise.Description",
        defaultMessage:
            "For large enterprises looking to manage projects at scale"
    });

    return (
        <EnterpriseCard
            planName="Enterprise"
            description={planDescription}
            isRecommendedPlan={recommended}
            dataProductKey={DATA_PRODUCT_KEY}
            ctaId={"planCtaEnterprise"}
            ctaText={ctaText}
            ctaUrl={contentfulCta?.ctaUrl ?? ctaUrl}
            offeringSku={jiraOfferingSkus.enterprise}
            planFeatureMessages={enterprisePlanFeatureMessages}
            adjacentLowerTierPlanName="Premium"
            unitCount={unitCount}
            unitTierLowerBound={unitTierLowerBound}
            unitTierUpperBound={unitTierUpperBound}
        />
    );
};
